/**
 * Maximum number of numbered page buttons to show.
 * @type {number}
 */
const NUM_BUTTONS = 5

/**
 * Pagination links component.
 */
export default class Pagination {

    /** @type {HTMLUListElement} */
    _element

    /** @type {number} */
    _numPages

    /** @type {number} */
    _currentPage

    /** @type {number} */
    _buttonsOffset

    /** @type {HTMLButtonElement} */
    _prevBtn

    /** @type {HTMLButtonElement} */
    _nextBtn

    /** @type {HTMLButtonElement[]} */
    _pageButtons = []

    /** @type {HTMLLIElement} */
    _prevPlaceholder

    /** @type {HTMLLIElement} */
    _nextPlaceholder

    /**
     * @callback changeHandler
     * @param {number} pageNumber The new current page number
     */

    /** @type {changeHandler} */
    _onChange

    /**
     * @param {Object} props
     * @param {number} props.numPages Total number of pages
     * @param {number} [props.currentPage] Index of the current page, starting from 1
     * @param {changeHandler} [props.onChange] Change handler, called when the current page number changes
     */
    constructor({ numPages, currentPage, onChange }) {
        this._numPages = numPages
        this._currentPage = currentPage || 1
        this._onChange = onChange
    }

    /** @type {HTMLUListElement} */
    get element() {
        if (!this._element) {
            this._element = document.createElement('ul')
            this._element.classList.add('pagination')
        }
        return this._element
    }

    /**
     * Number of the currently selected page.
     * @type {number}
     */
    get currentPage() { return this._currentPage }

    set currentPage(pageNumber) {
        this._currentPage = pageNumber
        this._updateButtons()
    }

    /** @type {number} */
    get numPages() { return this._numPages }

    set numPages(number) {
        if (number !== this._numPages) {
            this._numPages = number
            this._renderButtons()
            this._updateButtons()
        }
    }

    /**
     * Render this component.
     * @return {HTMLUListElement} The pagination list element
     */
    render() {
        this._renderButtons()
        this._updateButtons()
        return this.element
    }

    /**
     * Render the items with buttons into the list.
     */
    _renderButtons() {
        this.element.innerHTML = ''
        if (this._numPages < 2) return

        if (this._numPages > 3) {
            this._prevBtn = this._renderButton('<i class="i-caret-left"></i>')
        }

        if (this._numPages > NUM_BUTTONS) {
            this._prevPlaceholder = this._renderPlaceholder()
        }

        this._pageButtons = []
        for (let i = 0; i < this._numPages && i < NUM_BUTTONS; i++) {
            const btn = this._renderButton()
            this._pageButtons.push(btn)
        }

        if (this._numPages > NUM_BUTTONS) {
            this._nextPlaceholder = this._renderPlaceholder()
        }

        if (this._numPages > 3) {
            this._nextBtn = this._renderButton('<i class="i-caret-right"></i>')
        }
    }

    /**
     * Create an empty button, wrap it with an <li> element and append it to the list.
     * @param {string} [innerHTML=""] The button's content
     * @return {HTMLButtonElement} The rendered button
     */
    _renderButton(innerHTML = '') {
        const btn = document.createElement('button')
        btn.setAttribute('class', 'btn btn-default btn-narrower')
        btn.innerHTML = innerHTML
        btn.addEventListener('click', e => this._handleClick(btn))
        const item = document.createElement('li')
        item.append(btn)
        this.element.append(item)
        return btn
    }

    /**
     * Create a placeholder item and append it to the list.
     * @return {HTMLLIElement} The rendered placeholder element
     */
    _renderPlaceholder() {
        const item = document.createElement('li')
        item.setAttribute('class', 'pagination-placeholder')
        item.textContent = '...'
        this.element.append(item)
        return item
    }

    /**
     * Update visibility and content of the elements according to the current state.
     */
    _updateButtons() {
        if (this._prevPlaceholder) {
            this._prevPlaceholder.classList.toggle('hidden', this._currentPage <= Math.ceil(NUM_BUTTONS / 2))
        }
        if (this._nextPlaceholder) {
            const remaining = this._numPages - this._currentPage
            this._nextPlaceholder.classList.toggle('hidden', remaining <= Math.floor(NUM_BUTTONS / 2))
        }

        if (this._prevBtn) {
            this._prevBtn.classList.toggle('hidden', this._currentPage < 3)
        }
        if (this._nextBtn) {
            this._nextBtn.classList.toggle('hidden', this._currentPage > this._numPages - 2)
        }

        let offset = this._currentPage - Math.floor(NUM_BUTTONS / 2)
        const maxOffset = this._numPages + 1 - NUM_BUTTONS
        if (offset > maxOffset) {
            offset = maxOffset
        }
        this._buttonsOffset = (offset < 1) ? 1 : offset
        this._pageButtons.forEach((btn, i) => {
            const number = this._buttonsOffset + i
            const isCurrent = number === this._currentPage
            btn.classList.toggle('btn-primary', isCurrent)
            btn.classList.toggle('btn-default', !isCurrent)
            btn.textContent = number + ''
        })
    }

    /**
     * Handle a click on one of the buttons and change the current page according to the given number.
     * @param {HTMLButtonElement} btn The clicked button
     */
    _handleClick(btn) {
        if (btn === this._prevBtn) {
            this.currentPage--
        } else if (btn === this._nextBtn) {
            this.currentPage++
        } else {
            const btnIndex = this._pageButtons.indexOf(btn)
            if (btnIndex >= 0) this.currentPage = this._buttonsOffset + btnIndex
        }
        if (this._onChange) this._onChange(this.currentPage)
    }

}
